import { FunctionComponent, useState } from 'react'
import { ReservationChild, ReservationEvent, ReservationParent } from '@/types/EventDay.ts'
import { Table, TableBody, TableCell, TableRow, Tooltip } from '@mui/material'
import styles from './userReservationEvent.module.scss'
import { ChildCare, Delete, Face, Forum, KeyboardArrowDown, KeyboardArrowUp, StickyNote2 } from '@mui/icons-material'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ConfirmDeleteOneReservationDialog from '../confirmDeleteOneReservation/confirmDeleteOneReservationDialog.tsx'

interface UserReservationEventProps {
    reservation: ReservationEvent
    daycareId: number
    eventId: number
    showReservationGlobal: boolean
    canUpdate: boolean
    refetchAllReservations: () => Promise<void>
    openOrCloseUserReservation: (open: boolean) => void
}

const UserReservationEvent: FunctionComponent<UserReservationEventProps> = ({
    reservation,
    daycareId,
    eventId,
    showReservationGlobal,
    canUpdate,
    refetchAllReservations,
    openOrCloseUserReservation
}: UserReservationEventProps) => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const [showReservation, setShowReservation] = useState<boolean>(showReservationGlobal)
    const [showDeleteOneReservation, setShowDeleteOneReservationDialog] = useState<boolean>(false)

    const [childOrAdultId, setChildOrAdultId] = useState<number>(0)
    const [reservationIdForDelete, setReservationIdForDelete] = useState<number>(0)
    const [reservationEventIdForDelete, setReservationEventIdForDelete] = useState<number>(0)
    const [isParentForDelete, setIsParentForDelete] = useState<boolean>(false)

    function handleDeleteReservation(
        id: number,
        reservationId: number,
        reservationEventId: number,
        isParent: boolean
    ): void {
        setChildOrAdultId(id)
        setReservationIdForDelete(reservationId)
        setReservationEventIdForDelete(reservationEventId)
        setIsParentForDelete(isParent)

        setShowDeleteOneReservationDialog(true)
    }

    function handleShowReservation(): void {
        setShowReservation(!showReservation)
        openOrCloseUserReservation(!showReservation)
    }

    return (
        <div className={styles.TablePlacement}>
            <div className={styles.TitleContainer} onClick={handleShowReservation}>
                <div className={styles.Title}>
                    <p className={styles.Text}>{reservation.nameUser}</p>
                    {showReservation ? (
                        <KeyboardArrowUp className={styles.Icon} />
                    ) : (
                        <KeyboardArrowDown className={styles.Icon} />
                    )}
                </div>
                {!showReservation && (
                    <div className={styles.IndicatorsContainer}>
                        <div className={styles.Indicator}>
                            <Face className={styles.Icon} />
                            {reservation.reservationParent.length}
                        </div>
                        <div className={styles.Indicator}>
                            <ChildCare className={styles.Icon} />
                            {reservation.reservationChild.length}
                        </div>
                    </div>
                )}
            </div>
            {showReservation && (
                <>
                    <Table className={styles.Table}>
                        <TableBody>
                            {reservation.reservationChild
                                .sort((a: ReservationChild, b: ReservationChild) =>
                                    a.childName.localeCompare(b.childName)
                                )
                                .map((childReservation: ReservationChild) => (
                                    <TableRow key={childReservation.id}>
                                        <TableCell sx={{ width: '20%' }} align={'center'}>
                                            <Tooltip title={t('child')}>
                                                <ChildCare
                                                    className={styles.ClickableIcon}
                                                    onClick={() =>
                                                        navigate(`/childs?child=${childReservation.childId}`)
                                                    }
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: canUpdate ? '60%' : '80%' }}>
                                            {childReservation.childName}
                                        </TableCell>
                                        {canUpdate && (
                                            <TableCell sx={{ width: '20%' }}>
                                                <Delete
                                                    className={styles.ClickableIcon}
                                                    onClick={() =>
                                                        handleDeleteReservation(
                                                            childReservation.childId,
                                                            childReservation.id,
                                                            childReservation.reservationId,
                                                            false
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            {reservation.reservationParent
                                .sort((a: ReservationParent, b: ReservationParent) => a.name.localeCompare(b.name))
                                .map((adultReservation: ReservationParent) => (
                                    <TableRow key={adultReservation.id}>
                                        <TableCell sx={{ width: '20%' }} align={'center'}>
                                            <Tooltip title={t('adult')}>
                                                <Face
                                                    className={styles.ClickableIcon}
                                                    onClick={() =>
                                                        navigate(`/adults?parent=${adultReservation.parentId}`)
                                                    }
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ width: canUpdate ? '60%' : '80%' }}>
                                            {adultReservation.name}
                                        </TableCell>
                                        {canUpdate && (
                                            <TableCell sx={{ width: '20%' }}>
                                                <Delete
                                                    className={styles.ClickableIcon}
                                                    onClick={() =>
                                                        handleDeleteReservation(
                                                            adultReservation.parentId,
                                                            adultReservation.id,
                                                            adultReservation.reservationId,
                                                            true
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                    {(reservation.daycareComment || reservation.parentComment) && (
                        <>
                            {reservation.daycareComment && (
                                <div className={styles.Table}>
                                    <div className={styles.Container}>
                                        <StickyNote2 className={styles.IconDaycare} />
                                        <p className={styles.Text}>{reservation.daycareComment}</p>
                                    </div>
                                </div>
                            )}
                            {reservation.parentComment && (
                                <div className={styles.Table}>
                                    <div className={styles.Container}>
                                        <Forum className={styles.IconDaycare} />
                                        <p className={styles.Text}>{reservation.parentComment}</p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
            {showDeleteOneReservation && (
                <ConfirmDeleteOneReservationDialog
                    open={showDeleteOneReservation}
                    handleClose={() => setShowDeleteOneReservationDialog(false)}
                    reservationId={reservationIdForDelete}
                    daycareId={daycareId}
                    eventId={eventId}
                    reservationEventId={reservationEventIdForDelete}
                    childOrAdultId={childOrAdultId}
                    isParentForDelete={isParentForDelete}
                    refetchAllReservations={refetchAllReservations}
                />
            )}
        </div>
    )
}

export default UserReservationEvent
